/**
 * useApi Hook
 * 
 * React hook that provides authenticated API methods using MSAL tokens.
 * Automatically handles token acquisition and adds Authorization headers.
 */

import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from './authConfig';

/**
 * Hook providing authenticated API methods
 * @returns {object} API methods: authFetch, authGet, authPost, authPut, authDelete, getAccessToken
 */
export function useApi() {
    const { instance, accounts } = useMsal();

    /**
     * Acquire an access token silently, falling back to popup if needed
     */
    const getAccessToken = useCallback(async () => {
        if (accounts.length === 0) {
            console.error('No accounts found. User needs to sign in.');
            return null;
        }

        const account = accounts[0];

        try {
            const response = await instance.acquireTokenSilent({
                ...apiRequest,
                account: account
            });
            return response.accessToken;
        } catch (error) {
            console.warn('Silent token acquisition failed, attempting interactive:', error);
            
            try {
                const response = await instance.acquireTokenPopup(apiRequest);
                return response.accessToken;
            } catch (interactiveError) {
                console.error('Interactive token acquisition failed:', interactiveError);
                return null;
            }
        }
    }, [instance, accounts]);

    /**
     * Make an authenticated fetch request
     */
    const authFetch = useCallback(async (url, options = {}) => {
        const token = await getAccessToken();
        
        if (!token) {
            throw new Error('Unable to acquire access token for API call');
        }

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers
        };

        return fetch(url, {
            ...options,
            headers
        });
    }, [getAccessToken]);

    /**
     * GET request with authentication
     */
    const authGet = useCallback(async (url, options = {}) => {
        return authFetch(url, {
            method: 'GET',
            ...options
        });
    }, [authFetch]);

    /**
     * POST request with authentication
     */
    const authPost = useCallback(async (url, data, options = {}) => {
        return authFetch(url, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options
        });
    }, [authFetch]);

    /**
     * PUT request with authentication
     */
    const authPut = useCallback(async (url, data, options = {}) => {
        return authFetch(url, {
            method: 'PUT',
            body: JSON.stringify(data),
            ...options
        });
    }, [authFetch]);

    /**
     * DELETE request with authentication
     */
    const authDelete = useCallback(async (url, options = {}) => {
        return authFetch(url, {
            method: 'DELETE',
            ...options
        });
    }, [authFetch]);

    return {
        getAccessToken,
        authFetch,
        authGet,
        authPost,
        authPut,
        authDelete
    };
}

export default useApi;
